"use client";

import { useEffect, useRef, useState } from "react";

import { EuFlag, UkFlag } from "@/app/concept/groups/drawing-variant-regions";
import type { VenusIsolation, VenusRegion, VenusTuning } from "@/lib/waitlist/venus-enquiry";
import { DATA_LABEL_CLS, DISPLAY } from "./theme";
import { DedicatedServerDiagram, SharedServerDiagram, TuningChip } from "./venus-enquiry-diagrams";
import styles from "./venus-enquiry-form.module.css";

const STEP_COUNT = 5;
const QUESTION_COUNT = 3;
const AUTO_ADVANCE_MS = 420;

const QUESTION_CLS =
  "text-[clamp(1.9rem,4vw,3rem)] leading-[1.05] font-semibold tracking-[-0.03em]";

const QUESTIONS = [
  {
    key: "region",
    heading: "Where should your model live?",
    hint: "Private infrastructure, pinned to the country you choose.",
  },
  {
    key: "isolation",
    heading: "Share a server, or take your own?",
    hint: "Both are secure. The difference is whose workload sits on the hardware.",
  },
  {
    key: "tuning",
    heading: "Off the shelf, or cut to fit?",
    hint: "Fine-tuning teaches the model your terminology, policies and decision formats.",
  },
  {
    key: "email",
    heading: "Where should we reply?",
    hint: "Work email. We never share it.",
  },
  {
    key: "message",
    heading: "Anything else?",
    hint: "Optional: timelines, questions, constraints.",
  },
] as const;

type SubmitState = "idle" | "submitting" | "success" | "error";

function ChoiceCard({
  selected,
  onClick,
  title,
  sub,
  children,
  name,
  value,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  sub: string;
  children: React.ReactNode;
  name: string;
  value: string;
}) {
  return (
    <button
      type="button"
      className={styles.choiceCard}
      onClick={onClick}
      aria-pressed={selected}
      name={name}
      value={value}
    >
      {children}
      <span className={styles.choiceTitle}>{title}</span>
      <span className={styles.choiceSub}>{sub}</span>
    </button>
  );
}

export function VenusEnquiryForm() {
  const [state, setState] = useState<SubmitState>("idle");
  const [step, setStep] = useState(0);
  const [region, setRegion] = useState<VenusRegion | null>(null);
  const [isolation, setIsolation] = useState<VenusIsolation | null>(null);
  const [tuning, setTuning] = useState<VenusTuning | null>(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const questionRef = useRef<HTMLHeadingElement>(null);
  const stepRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedStep = useRef(false);

  useEffect(() => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    return () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    };
  }, [step]);

  // Keep the current question centred and announced; skip the initial mount.
  useEffect(() => {
    if (!mountedStep.current) {
      mountedStep.current = true;
      return;
    }
    const heading = questionRef.current;
    if (heading) {
      heading.focus({ preventScroll: true });
    }
    stepRef.current?.scrollIntoView({
      block: "center",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, [step]);

  function chooseStepAction(setter: () => void) {
    setter();
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(
      () => setStep((current) => Math.min(current + 1, STEP_COUNT - 1)),
      AUTO_ADVANCE_MS,
    );
  }

  function goBack() {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    setStep((current) => Math.max(current - 1, 0));
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState("submitting");
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15_000);
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          venus_region: region ?? undefined,
          venus_isolation: isolation ?? undefined,
          venus_tuning: tuning ?? undefined,
          venus_message: message.trim() || undefined,
          source_path: "/research",
          cta_placement: "direct",
        }),
        signal: controller.signal,
      }).finally(() => clearTimeout(timeout));
      if (!response.ok) throw new Error(`enquiry failed: ${response.status}`);
      setState("success");
    } catch {
      setState("error");
    }
  }

  if (state === "success") {
    return (
      <div
        data-venus-enquiry="success"
        className="border-y border-[var(--rule)] px-5 py-8 text-[1rem] leading-[1.65] text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]"
      >
        Thank you, your enquiry is in. The Project Venus team reads every
        enquiry and will reply to your work email. If it is urgent, email
        research@mlx.systems directly.
      </div>
    );
  }

  const question = QUESTIONS[step];
  if (!question) return null;

  return (
    <form
      data-venus-enquiry={state}
      onSubmit={onSubmit}
      className={`${styles.root} border-y border-[var(--rule)]`}
      aria-label="Project Venus enquiry"
    >
      <div className="flex items-center justify-between gap-6 border-b border-dotted border-[var(--rule)] px-5 py-4">
        <p className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}>
          {step < QUESTION_COUNT
            ? `Question ${step + 1} of ${QUESTION_COUNT}`
            : step === STEP_COUNT - 1
              ? "Last step"
              : "Your email"}
        </p>
        <div className={styles.progress} aria-hidden="true">
          {Array.from({ length: STEP_COUNT }, (_, index) => (
            <span key={index} data-filled={index <= step} />
          ))}
        </div>
        {step > 0 ? (
          <button type="button" className={styles.backLink} onClick={goBack}>
            ← Back
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
      </div>

      <div ref={stepRef} className="px-5 py-12 md:py-16">
        <div className={styles.step} key={question.key}>
          <h3
            ref={questionRef}
            tabIndex={-1}
            className={`${QUESTION_CLS} max-w-[24ch] text-[var(--theme-ground)] outline-none`}
            style={DISPLAY}
          >
            {question.heading}
          </h3>
          <p className="mt-4 max-w-[38rem] text-[0.95rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ground)_66%,transparent)]">
            {question.hint}
          </p>

          <div className="mt-10">
            {question.key === "region" && (
              <div className={styles.choiceGrid} role="group" aria-label="Hosting region">
                <ChoiceCard
                  name="venus_region"
                  value="uk"
                  selected={region === "uk"}
                  onClick={() => chooseStepAction(() => setRegion("uk"))}
                  title="United Kingdom"
                  sub="UK datacentre, end to end"
                >
                  <svg viewBox="0 0 100 66.67" width="108" height="72" role="img" aria-label="United Kingdom">
                    <UkFlag x={0} y={0} width={100} height={66.67} />
                  </svg>
                </ChoiceCard>
                <ChoiceCard
                  name="venus_region"
                  value="eu"
                  selected={region === "eu"}
                  onClick={() => chooseStepAction(() => setRegion("eu"))}
                  title="European Union"
                  sub="EU datacentre, end to end"
                >
                  <svg viewBox="0 0 100 66.67" width="108" height="72" role="img" aria-label="European Union">
                    <EuFlag x={0} y={0} width={100} height={66.67} />
                  </svg>
                </ChoiceCard>
              </div>
            )}

            {question.key === "isolation" && (
              <div className={styles.choiceGrid} role="group" aria-label="Server isolation">
                <ChoiceCard
                  name="venus_isolation"
                  value="colocated"
                  selected={isolation === "colocated"}
                  onClick={() => chooseStepAction(() => setIsolation("colocated"))}
                  title="Share a server"
                  sub="Your bay, padlocked apart from other MLX tenants"
                >
                  <SharedServerDiagram />
                </ChoiceCard>
                <ChoiceCard
                  name="venus_isolation"
                  value="dedicated"
                  selected={isolation === "dedicated"}
                  onClick={() => chooseStepAction(() => setIsolation("dedicated"))}
                  title="Take my own"
                  sub="A physical instance that runs nothing but you"
                >
                  <DedicatedServerDiagram />
                </ChoiceCard>
              </div>
            )}

            {question.key === "tuning" && (
              <div>
                <div className={styles.dial} aria-hidden="true">
                  <span className={styles.dialTrack} />
                  <span
                    className={styles.dialTraveler}
                    data-side={
                      tuning === "standard" ? "left" : tuning === "fine-tuned" ? "right" : "none"
                    }
                  />
                </div>
                <div className={styles.choiceGrid} role="group" aria-label="Model tuning">
                  <ChoiceCard
                    name="venus_tuning"
                    value="standard"
                    selected={tuning === "standard"}
                    onClick={() => chooseStepAction(() => setTuning("standard"))}
                    title="Off the shelf"
                    sub="A strong general model, ready now"
                  >
                    <TuningChip tuned={false} />
                  </ChoiceCard>
                  <ChoiceCard
                    name="venus_tuning"
                    value="fine-tuned"
                    selected={tuning === "fine-tuned"}
                    onClick={() => chooseStepAction(() => setTuning("fine-tuned"))}
                    title="Cut to fit"
                    sub="Fine-tuned to your business"
                  >
                    <TuningChip tuned />
                  </ChoiceCard>
                </div>
              </div>
            )}

            {question.key === "email" && (
              <div className="mx-auto flex max-w-[34rem] flex-col items-center gap-6">
                <input
                  ref={emailInputRef}
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                  aria-label="Work email"
                  className={styles.emailInput}
                />
                <button
                  type="button"
                  onClick={() => {
                    const input = emailInputRef.current;
                    if (!input || input.reportValidity()) setStep(STEP_COUNT - 1);
                  }}
                  className="bg-[var(--theme-ground)] px-6 py-3 text-[0.95rem] font-semibold text-[var(--theme-accent-text)] transition-opacity hover:opacity-90"
                >
                  Continue
                </button>
              </div>
            )}

            {question.key === "message" && (
              <div className="max-w-[42rem]">
                <div className={styles.recap} aria-label="Your answers">
                  <button type="button" onClick={() => setStep(0)}>
                    <span className={styles.recapKey}>Region</span>
                    {region === "uk" ? "UK" : "EU"}
                  </button>
                  <button type="button" onClick={() => setStep(1)}>
                    <span className={styles.recapKey}>Server</span>
                    {isolation === "dedicated" ? "My own instance" : "Shared"}
                  </button>
                  <button type="button" onClick={() => setStep(2)}>
                    <span className={styles.recapKey}>Model</span>
                    {tuning === "fine-tuned" ? "Fine-tuned" : "Off the shelf"}
                  </button>
                  <button type="button" onClick={() => setStep(3)}>
                    <span className={styles.recapKey}>Reply to</span>
                    {email}
                  </button>
                </div>
                <textarea
                  name="venus_message"
                  rows={4}
                  maxLength={2_000}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Anything worth knowing: timelines, questions, constraints."
                  aria-label="Anything else you would like to add"
                  className="mt-6 w-full border border-[color-mix(in_srgb,var(--theme-ground)_32%,transparent)] bg-transparent px-3 py-2.5 text-[var(--theme-ground)] outline-none focus-visible:border-[var(--theme-accent)]"
                />
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={state === "submitting"}
                    className="bg-[var(--theme-ground)] px-5 py-3 text-[0.95rem] font-semibold text-[var(--theme-accent-text)] transition-opacity hover:opacity-90 disabled:opacity-60"
                  >
                    {state === "submitting" ? "Sending…" : "Send enquiry"}
                  </button>
                  {state === "error" ? (
                    <p
                      role="alert"
                      className="text-[0.9rem] text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]"
                    >
                      That did not go through. Please try again, or email research@mlx.systems.
                    </p>
                  ) : (
                    <p className={styles.note}>
                      Replies come from the Project Venus team. We never share your details.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
